import axios from 'axios';

// API 기본 설정
// 프로덕션: REACT_APP_API_URL='' (빈 문자열) → baseURL='/api' (상대 경로)
// 개발: REACT_APP_API_URL='http://localhost:8000' → baseURL='http://localhost:8000/api'
const API_URL = process.env.REACT_APP_API_URL ?? '';
let authSessionVersion = 0;
export const invalidateAuthSession = () => { authSessionVersion += 1; };

// 인증 관련 localStorage 키 일괄 정리 (세션 만료/종료 시 일관 처리 — kakao_user 등 잔존 방지)
const clearAuthStorage = () => {
  invalidateAuthSession();
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('kakao_user');
  propertyApi.clearCache();
  window.dispatchEvent(new Event('voonang:auth-cleared'));
};

// Axios 인스턴스 생성
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터: 인증 토큰 자동 추가
api.interceptors.request.use(
  (config) => {
    (config as any)._authSessionVersion = authSessionVersion;
    (config as any)._authScope = authScope();
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 401 에러 시 토큰 갱신 시도 후 재시도
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401 Unauthorized && 토큰 갱신 시도하지 않은 요청
    if (error.response?.status === 401 && !originalRequest._retry) {
      // A request from a session that has ended must not refresh or revive it.
      const requestSession = originalRequest._authSessionVersion;
      const requestScope = originalRequest._authScope;
      const sessionChanged = () => requestSession !== authSessionVersion || requestScope !== authScope();
      if (sessionChanged()) {
        return Promise.reject(new axios.CanceledError('Authentication changed'));
      }
      const sentAuthorization = originalRequest.headers?.Authorization;
      const currentToken = localStorage.getItem('access_token');
      if (currentToken && sentAuthorization !== `Bearer ${currentToken}`) {
        // Another request already renewed this same session. Retry with that token.
        originalRequest._retry = true;
        return api(originalRequest);
      }
      if (isRefreshing) {
        // 이미 토큰 갱신 중이면 대기열에 추가
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (sessionChanged()) throw new axios.CanceledError('Authentication changed');
            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        isRefreshing = false;
        processQueue(error, null);
        // Refresh 토큰 없으면 로그인 페이지로 (모든 인증 키 일괄 정리)
        clearAuthStorage();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // 토큰 갱신 시도
        const response = await axios.post(`${API_URL}/api/auth/token/refresh/`, {
          refresh: refreshToken,
        });

        const newAccessToken = response.data.access;
        if (sessionChanged() || localStorage.getItem('refresh_token') !== refreshToken) {
          throw new axios.CanceledError('Authentication changed');
        }
        localStorage.setItem('access_token', newAccessToken);
        // 서버가 refresh 를 회전해 새 값을 주면 그것으로 교체 (2026-09-25 — 회전 켜도 로그아웃 안 되게 미리 대응)
        if (response.data.refresh) {
          localStorage.setItem('refresh_token', response.data.refresh);
        }

        // 대기 중인 요청들에게 새 토큰 전달
        processQueue(null, newAccessToken);

        // 원래 요청 재시도
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // 토큰 갱신 실패 시 로그아웃 처리 (모든 인증 키 일괄 정리)
        processQueue(refreshError, null);
        if (!sessionChanged() && localStorage.getItem('refresh_token') === refreshToken) {
          clearAuthStorage();
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// 타입 정의
export interface User {
  id: number;
  username: string;
  email: string;
  phone?: string;
}

export interface FloorPlan {
  id: number;
  type_name: string;
  area: number;
  image_url: string; // 원본 평면도 (최적화됨, 품질 90)
  image_thumbnail?: string; // 썸네일 (300x300)
  description?: string;
  order: number;
}

export interface DiscountBenefit {
  id: number;
  title: string;
  description: string;
  image?: string; // 원본 혜택 이미지 (최적화됨)
  image_thumbnail?: string; // 썸네일 (300x200)
  discount_amount?: number;
  discount_rate?: number;
  valid_from?: string;
  valid_until?: string;
  is_active: boolean;
  // 구조화 필드 (benefit_design.md 1장). 출처·확신도 등 내부용 필드는 API 에 없음.
  unit_type?: '' | 'apt' | 'officetel' | 'commercial';
  kind?: string; // price_cut | payment | free_option | guarantee | cashback | tax | etc
  discount_rate_min?: number | null;
  is_headline?: boolean;
  verified_at?: string | null;
}

/** 대표 혜택 — 서버(services/benefits.py)가 계산. 프론트는 렌더만 한다. */
export interface BenefitHeadline {
  text: string;
  sub: string | null;
  kind: string;
  unit_type: string;
  rate_max: number | null;
  rate_min: number | null;
  amount: number | null;
  benefit_id: number;
}

export interface DiscountBenefitRestricted {
  count: number;
  /** 비로그인 — 카카오 로그인하면 보인다 */
  login_required?: true;
  /** 로그인했지만 이 현장 혜택 안내를 아직 요청하지 않음 — 요청(benefit-lead)하면 바로 보인다 (2026-10-08) */
  request_required?: true;
  message: string;
}

// 현장 실제 사진 갤러리
export interface GalleryImage {
  id: number;
  image: string;           // 원본 이미지 URL
  image_thumbnail: string; // 200x200 썸네일
  image_medium: string;    // 600x600 중간 사이즈
  caption: string | null;  // 사진 설명
  orientation: 'horizontal' | 'vertical' | 'auto';  // 사진 방향
  order: number;
}

// 평형별 분양가/실거래가
export interface PropertyTypePrice {
  type_name: string;
  exclusive_area: number | null;
  supply_area: number | null;
  top_price: number | null;
  min_price?: number | null; // 공고문 최저가(확정값만, 2026-10-01) — 없으면 최고가만 표시
  supply_count: number | null;
  recent_deal_price: number | null;
  recent_deal_date: string | null;
  deal_count: number;
  // v2 (2026-09-21) — 서버 계산값
  price_per_pyeong?: number | null; // 평당가(만원) = top_price / (supply_area/3.3058), 둘 다 >0 일 때만
  deal_compare_pct?: number | null; // (top_price - recent_deal_price)/recent_deal_price*100, deal_count>=3 일 때만
}

export interface Property {
  id: number;
  title: string;
  developer?: string;
  constructor?: string;
  property_type: string; // 아파트, 오피스텔, 도시형생활주택, 상가/업무시설
  status: string; // 분양예정, 마감임박, 분양중, 분양완료, 계약완료
  business_type?: string; // 사업유형 (분양/임대/분양임대)
  is_side_inquiry?: boolean; // 사이드문의 여부 (담당자 없는 현장)
  is_pre_registration?: boolean; // 관심고객 사전등록 여부 (담당자 배정 전 모집)
  is_reopen?: boolean; // 즉시입주 잔여세대(입주 후 미분양 재오픈) — 2026-09-28

  // 특별현장 설정 (지도에서 커스텀 마커 표시)
  is_special?: boolean; // 특별현장 여부
  special_marker_text?: string | null; // 마커에 표시할 텍스트
  special_marker_subtext?: string | null; // 마커 하단 서브텍스트
  special_order?: number; // 특별현장 정렬 순서

  address: string;
  latitude?: number;
  longitude?: number;
  total_units?: number;
  officetel_units?: number | null; // 그중 오피스텔 실 수(혼합 단지) — 2026-10-01
  building_scale?: string; // 건물 규모 (예: 지하2층~지상29층)
  parking_spaces?: number; // 주차대수
  min_area?: number;
  max_area?: number;
  min_price?: number;
  max_price?: number;
  subscription_start?: string;
  subscription_end?: string;
  move_in_date?: string;
  // 청약 일정 6단계 (v2 2026-09-21 — 상세 API 가 이미 내려주는 필드, 타입만 선언)
  announcement_date?: string | null; // 입주자 모집공고일
  special_supply_date?: string | null; // 특별공급
  general_rank1_date?: string | null; // 1순위
  general_rank2_date?: string | null; // 2순위
  winner_announcement_date?: string | null; // 당첨자 발표
  contract_start?: string | null; // 정당계약 시작
  contract_end?: string | null; // 정당계약 종료
  description?: string;
  features?: string;

  // 이미지 필드들 - ImageKit 최적화 적용
  image_url?: string; // 원본 대표 이미지 (최적화됨, 최대 1920x1440)
  image_thumbnail?: string; // 썸네일 (300x300, 목록용)
  image_card?: string; // 카드 이미지 (400x300, 카드형 목록용)
  image_detail?: string; // 상세 이미지 (1200x900, 상세 페이지용)

  exterior_image?: string; // 원본 외관 이미지
  exterior_thumbnail?: string; // 외관 썸네일 (400x300)

  complex_layout_image?: string; // 원본 단지배치도
  complex_layout_thumbnail?: string; // 단지배치도 썸네일 (400x300)

  // 섹션별 상세 이미지들
  business_overview_image?: string; // 사업개요 이미지
  brand_intro_image?: string; // 브랜드소개 이미지
  location_image?: string; // 입지환경 이미지
  premium_image?: string; // 프리미엄 이미지
  community_image?: string; // 커뮤니티 이미지
  specialization_image?: string; // 단지특화 이미지
  design_image?: string; // 단지설계 이미지
  landscape_image?: string; // 조경시설 이미지
  system_image?: string; // 시스템 이미지

  // 블로그형 상세 설명
  detailed_description?: string; // 블로그 형식의 정보전달형 글

  // 실거래가 정보
  recent_deal_price?: number | null;
  recent_deal_date?: string | null;
  deal_count?: number | null;

  contact_number?: string;
  model_house_phone?: string | null; // 모델하우스/현장 전화 (청약홈 MDHS_TELNO)
  // v2 (2026-09-21)
  hug_guarantee?: boolean; // HUG 분양보증
  model_house_address?: string | null; // 모델하우스 주소 (비면 섹션 미노출)
  price_per_pyeong?: number | null; // 평당가(만원) — type_prices 중 top_price 최저 행 기준
  favorite_count?: number | null; // 상세 전용. 관심등록 수, 10 미만이면 null
  homepage_url?: string;
  house_manage_no?: string | null; // 주택관리번호 (청약홈 고유 ID)
  created_at?: string;
  updated_at?: string;
  is_favorited?: boolean;
  floor_plans?: FloorPlan[];
  discount_benefits?: DiscountBenefit[] | DiscountBenefitRestricted; // members_only·비로그인이면 dict
  benefit_headline?: BenefitHeadline | null; // 대표 혜택 (지도 마커·상세 상단)
  has_active_benefit?: boolean; // map API: 활성·미만료 혜택 1건 이상 → 배너 마커 (대표 지시 2026-09-21)
  has_any_benefit?: boolean; // map API: 활성 혜택 1건 이상(배지와 무관) — 지도 상태 '분양중' 고정용 (2026-10-06)
  banner_text?: string | null; // map·목록·상세 API: 배너 마커·배지·상세 칩 본문 (서버 services/benefits.build_banner_text — 할인율→할인금액→가격→그 외)
  banner_subtext?: string | null; // map·목록·상세 API: 배너 서브·배지 2번째 (가격 보조 → 수동 서브 → null)
  banner_kind?: 'rate' | 'amount' | 'price' | 'manual' | 'benefit' | null; // banner_text 단계. 'price'(원분양가/할인가)면 '대표혜택' 라벨·배지 금지 (2026-09-22 대표)
  gallery_images?: GalleryImage[];  // 현장 실제 사진 갤러리
  type_prices?: PropertyTypePrice[];  // 평형별 분양가/실거래가
  supply_area_min?: number | null;  // 지도 API — 평형 공급면적 최소(㎡), 평수 표기용 (2026-09-26)
  supply_area_max?: number | null;  // 지도 API — 평형 공급면적 최대(㎡)

  // 검색량 (관리자 수동 입력, map_data API 노출)
  naver_search_volume?: number;
  search_volume_month?: number | null;

  // Phase 7 — ProcessedImageField 변형 URL (LCP 최적화)
  // image_card_url: 400x300 카드용
  // image_thumbnail_url: 300x300 작은 썸네일
  // 둘 다 imagekit lazy generation, null이면 image_url로 fallback
  image_card_url?: string | null;
  image_thumbnail_url?: string | null;
}

export interface Presale {
  id: number;
  name: string;
  brand?: string;
  theme?: string;
  address: string;
  description?: string;
  image_url?: string;
  supply_date?: string;
  created_at?: string;
  updated_at?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  phone?: string;
}

export interface AuthResponse {
  access: string;
  refresh: string;
  user: User;
}

export interface RefreshTokenResponse {
  access: string;
}

// 인증 API
export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post('/auth/login/', credentials);
    return response.data;
  },

  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response = await api.post('/auth/register/', data);
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response = await api.get('/auth/profile/');
    return response.data;
  },

  refreshToken: async (refresh: string): Promise<RefreshTokenResponse> => {
    const response = await api.post('/auth/token/refresh/', { refresh });
    return response.data;
  },

  logout: async (): Promise<void> => {
    // 서버에 로그아웃 요청 (선택사항)
    // await api.post('/auth/logout/');

    // 클라이언트 측 토큰 정리
    clearAuthStorage();
  },

  // 프로필 수정
  updateProfile: async (data: { username?: string; email?: string; phone?: string }): Promise<{ message: string; user: User }> => {
    const response = await api.patch('/auth/profile/update/', data);
    return response.data;
  },

  // 회원 탈퇴
  deleteAccount: async (): Promise<{ message: string }> => {
    const response = await api.delete('/auth/delete/');
    return response.data;
  },

  // 카카오 OAuth
  kakaoGetAuthUrl: async (state?: string): Promise<{ auth_url: string }> => {
    const response = await api.get('/auth/kakao/login/', { params: state ? { state } : undefined });
    return response.data;
  },

  kakaoCallback: async (code: string, consentMarketing?: boolean): Promise<KakaoAuthResponse> => {
    const response = await api.post('/auth/kakao/callback/', {
      code,
      consent_marketing: consentMarketing ?? false
    });
    return response.data;
  },
};

// 카카오 인증 응답 타입
interface KakaoUser {
  kakao_id: string;
  nickname: string | null;
  email: string | null;
  phone_number: string | null;
  profile_image_url: string | null;
}

interface KakaoAuthResponse {
  user: User;
  kakao_user: KakaoUser;
  access: string;
  refresh: string;
  is_new_user: boolean;
}

// 매물 API (신규 분양 현장)
// 프로퍼티 리스트 캐시 (메모리 + sessionStorage)
let propertiesCache: Property[] | null = null;
let cacheTimestamp: number = 0;
let cacheScope: string | null = null;
let cacheGeneration = 0;
// Never persist member benefit payloads in sessionStorage. Old unscoped entries
// are removed by clearCache and are deliberately not read.
const authScope = (): string => {
  const token = localStorage.getItem('access_token');
  if (!token) return 'anonymous';
  try {
    const id = JSON.parse(localStorage.getItem('user') || 'null')?.id;
    if (id != null) return `member:${id}`;
  } catch { /* invalid stored user cannot share a member cache */ }
  return token;
};
const assertCurrentScope = (scope: string, generation: number) => {
  if (scope !== authScope() || generation !== cacheGeneration) {
    throw new axios.CanceledError('Authentication changed');
  }
};
// 60초 캐시: 관리자가 현장 값(상태/가격/노출/특별현장)을 변경하면 이미 열려있는 세션에도
// 최대 60초 내 반영됨(기존 5분은 staleness 가 길어 변경 미반영 혼란을 유발했음).
const CACHE_DURATION = 60 * 1000;

// Map 데이터 캐시 (경량, 메모리만)
let mapDataCache: Property[] | null = null;
let mapCacheTimestamp: number = 0;
let mapCacheScope: string | null = null;

export const propertyApi = {
  getList: async (params?: {
    property_type?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    search?: string;
  }) => {
    const scope = authScope();
    const generation = cacheGeneration;
    // 파라미터 없는 기본 호출일 때만 캐시 사용
    if (!params || Object.keys(params).length === 0) {
      const now = Date.now();

      // 메모리 캐시 확인
      if (propertiesCache && cacheScope === scope && (now - cacheTimestamp) < CACHE_DURATION) {
        return propertiesCache;
      }
    }

    const response = await api.get('/properties/', { params });
    assertCurrentScope(scope, generation);
    const data = response.data;

    // 파라미터 없는 기본 호출일 때만 캐시 저장
    if (!params || Object.keys(params).length === 0) {
      propertiesCache = data;
      cacheTimestamp = Date.now();
      cacheScope = scope;
    }

    return data;
  },

  // 캐시 초기화 (목록 + 지도 캐시 모두) — 관리자 변경 즉시 반영이 필요할 때 호출
  clearCache: () => {
    cacheGeneration += 1;
    cacheScope = null;
    mapCacheScope = null;
    propertiesCache = null;
    cacheTimestamp = 0;
    mapDataCache = null;
    mapCacheTimestamp = 0;
    try {
      sessionStorage.removeItem('properties_cache');
      sessionStorage.removeItem('properties_cache_time');
    } catch (e) {}
  },

  // 프리로드 (앱 시작 시 호출)
  preload: async () => {
    if (!propertiesCache || cacheScope !== authScope()) {
      try {
        await propertyApi.getList();
      } catch (e) {
        // 프리로드 실패 무시
      }
    }
  },

  // ID 또는 house_manage_no(주택관리번호)로 조회 지원
  // - 일반 ID: /properties/44/
  // - house_manage_no: /properties/2021000959/ (10자리 이상 숫자)
  getDetail: async (id: number | string): Promise<Property> => {
    const scope = authScope();
    const generation = cacheGeneration;
    const response = await api.get(`/properties/${id}/`);
    assertCurrentScope(scope, generation);
    return response.data;
  },

  search: async (params: { lat: number; lng: number; radius: number }) => {
    const response = await api.get('/properties/nearby/', { params });
    return response.data;
  },

  // 지도/리스트 경량 API (90% 데이터 감소, 1초 로딩)
  getMapData: async (): Promise<Property[]> => {
    const scope = authScope();
    const generation = cacheGeneration;
    const now = Date.now();
    if (mapDataCache && mapCacheScope === scope && (now - mapCacheTimestamp) < CACHE_DURATION) {
      return mapDataCache;
    }
    const response = await api.get('/properties/map/');
    assertCurrentScope(scope, generation);
    mapDataCache = response.data;
    mapCacheScope = scope;
    mapCacheTimestamp = now;
    return response.data;
  },

  // 완판(분양완료) 현장 — 지도에 '완판' 마커로만 표시 (2026-09-26 대표: 긴박감). 목록·카드에는 쓰지 않는다.
  getSoldOutMapData: async (): Promise<Property[]> => {
    const response = await api.get('/properties/map/', { params: { sold_out: 1 } });
    return response.data;
  },

  // 자동완성 API (토큰 기반 검색: 대명힐 → 힐스테이트 대명센트럴 2차)
  autocomplete: async (query: string): Promise<Property[]> => {
    if (!query || query.length < 2) return [];
    const response = await api.get('/properties/autocomplete/', {
      params: { q: query }
    });
    return response.data;
  },

  // 문의하기 API (상담 신청 / 사이드 문의)
  contact: async (propertyId: number, data: {
    customer_name: string;
    customer_phone: string;
    property_name?: string;
    // 광고 추적(개인정보 아님): CAPI 중복 제거 event_id · _fbp/_fbc · 페이지 URL · 첫 진입 utm
    [trackingKey: string]: string | null | undefined;
  }): Promise<{ message: string }> => {
    const response = await api.post(`/properties/${propertyId}/contact/`, data);
    return response.data;
  },

  // 할인 혜택 알림 신청 API
  subscribeBenefitNotification: async (propertyId: number): Promise<{ message: string }> => {
    const response = await api.post(`/properties/${propertyId}/subscribe-benefit-notification/`);
    return response.data;
  },

  // 혜택 열람 리드 (2026-10-01) — 로그인 회원이 잠긴 현장 혜택을 볼 때 1회. 서버가 잠금 대상·중복을 판정
  recordBenefitLead: async (propertyId: number): Promise<{ created: boolean; reason?: string }> => {
    const response = await api.post(`/properties/${propertyId}/benefit-lead/`);
    return response.data;
  },
};

// 사전상담/알림신청 API
export const preConsultationApi = {
  submit: async (data: {
    property_id: number;
    type: 'consultation' | 'alert';
    name: string;
    phone: string;
    message?: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/pre-consultation/', data);
    return response.data;
  },
};

// 분양 API
export const presaleApi = {
  getList: async (params?: {
    brand?: string;
    search?: string;
  }) => {
    const response = await api.get('/presales/', { params });
    return response.data;
  },

  getDetail: async (id: number): Promise<Presale> => {
    const response = await api.get(`/presales/${id}/`);
    return response.data;
  },
};

// 찜하기 API
export const favoriteApi = {
  getList: async () => {
    const response = await api.get('/favorites/');
    return response.data;
  },

  toggle: async (propertyId: number) => {
    const response = await api.post('/favorites/toggle/', { property_id: propertyId });
    return response.data;
  },
};

// 최근 본 매물 API
export const recentViewApi = {
  getList: async () => {
    const response = await api.get('/recent/');
    return response.data;
  },
};

// 사이트 설정 타입
export interface SiteSettings {
  logo: string | null;
  theme_color: string;  // 테마 색상 (예: #FF6B00)
  kakao_javascript_key: string;
  kakao_rest_api_key: string | null;
  site_name: string;
  site_description: string;
  contact_phone: string;
  contact_email: string | null;
  discount_visibility: 'public' | 'members_only';
  benefit_notification_enabled: boolean;  // 할인 혜택 알림 신청 기능 활성화
  marker_icons: {
    [key: string]: string;  // 상태별 마커 아이콘 URL
  };
  head_scripts: string | null;  // HEAD 태그 직전 삽입 스크립트
  body_scripts: string | null;  // BODY 태그 직전 삽입 스크립트
  // 광고·분석 스크립트 (관리자 입력 ID, 2026-09-22 — utils/tracking 이 동적 로드). 구 백엔드 응답엔 없음
  meta_pixel_enabled?: boolean;
  meta_pixel_id?: string;
  facebook_domain_verification?: string;
  ga4_enabled?: boolean;
  ga4_measurement_id?: string;
  google_ads_enabled?: boolean;
  google_ads_id?: string;
  google_ads_lead_label?: string;
  google_ads_call_label?: string;
  kakao_pixel_enabled?: boolean;
  kakao_pixel_id?: string;
  naver_wcs_enabled?: boolean;
  naver_wcs_id?: string;
  clickbaro_enabled?: boolean;  // clickbaro 텔레그램 알림 (방문·문의·전화)
  clickbaro_script_url?: string;
  head_extra_html?: string;
}

// 사이트 설정 API
export const settingsApi = {
  get: async (): Promise<SiteSettings> => {
    const response = await api.get('/settings/');
    return response.data;
  },
};

// 알림 관련 타입
export interface Notification {
  id: number;
  property_id: number;
  property_title: string;
  house_manage_no?: string | null;  // 주택관리번호
  property_image: string | null;
  benefit_title: string;
  discount_text: string;
  valid_until: string | null;
  is_favorite: boolean;
  source: 'favorite' | 'recent';
  created_at: string | null;
}

export interface NotificationResponse {
  notifications: Notification[];
  unread_count: number;
}

// 알림 API
export const notificationApi = {
  getList: async (): Promise<NotificationResponse> => {
    const response = await api.get('/notifications/');
    return response.data;
  },
};

// ─────────────────────────────────────────────
// 지도 필터칩 (admin 에서 관리 — 2026-05-19)
// ─────────────────────────────────────────────
export type MapFilterType =
  | 'side_inquiry_false'
  | 'side_inquiry_true'
  | 'is_pre_registration'
  | 'min_area_gte'
  | 'min_area_lt'
  | 'total_units_gte'
  | 'status_eq'
  | 'status_in'
  | 'business_type_eq'
  | 'is_special';

export interface MapFilterChip {
  id: number;
  label: string;
  filter_type: MapFilterType;
  value: string;
  order: number;
  emphasis: boolean;
}

export const mapFilterApi = {
  list: async (): Promise<MapFilterChip[]> => {
    const response = await api.get('/map-filters/');
    return response.data;
  },
};

// ─────────────────────────────────────────────
// 리스트 페이지 필터 프리셋 (admin 에서 관리 — 2026-05-19)
// ─────────────────────────────────────────────
export type ListFilterType = 'area' | 'units' | 'move_in';

export interface ListFilterPreset {
  id: number;
  filter_type: ListFilterType;
  key: string;
  label: string;
  min_value: number | null;
  max_value: number | null;
  order: number;
}

export interface ListFilterPresetGroups {
  area: ListFilterPreset[];
  units: ListFilterPreset[];
  move_in: ListFilterPreset[];
}

export const listFilterApi = {
  groups: async (): Promise<ListFilterPresetGroups> => {
    const response = await api.get('/list-filters/');
    return response.data;
  },
};

export default api;
